/**
 * Remote Access Tools endpoints.
 *
 * Talks to `backend/src/routes/remote-tools.routes.js` (apps/api) —
 * `hostname` is the Intune `device.deviceName` (lower-cased server-side).
 * All routes require an Entra Bearer token (attached automatically by
 * apiGet/apiPost/etc, see api/client.js) plus the `remote-tools`
 * permission.
 *
 * Two endpoints (file download, and the admin shell WebSocket) can't go
 * through the generic apiGet/apiPost helpers: a browser `<a download>` /
 * `WebSocket` can't attach an `Authorization` header, so both acquire a
 * token directly via acquireApiToken() and pass it as a query string
 * (`?token=`) instead, mirroring OmniDiag's approach.
 */

import { apiGet, apiPost, apiPut, apiDelete, buildQuery } from '../client.js';
import { acquireApiToken } from '../../auth/msal.js';
import { API_BASE_URL } from '../../auth/config.js';

const base = (hostname) => `/api/remote-tools/${encodeURIComponent(hostname)}`;

/* ============================================================
 * Status / snapshot / live data
 * ============================================================ */

export const fetchRemoteStatus   = (hostname) => apiGet(`${base(hostname)}/status`);
// Android only: ask the phone (via Firebase) to start its agent and reconnect.
// { wake: { status: 'sent'|'throttled'|'no-token'|'not-configured'|'failed'|'connected', error? } }
export const wakeRemoteAgent     = (hostname) => apiPost(`${base(hostname)}/wake`);
// Bulk agent registry — one call for every enrolled agent:
// { success, agents: [{ hostname (lowercased short name), agentType,
//   platform, serialNumber, lastSeen, online }] }. Used by list views
// (e.g. Asset Management) to decorate hundreds of rows without a
// per-hostname status fetch each.
export const fetchAllAgents      = () => apiGet('/api/remote-tools/agents');
// Management list — INCLUDES deactivated agents, which fetchAllAgents filters
// out (it backs fleet counts and the Remote Tools button). Device keys are
// never returned. Also reports whether a reinstall can be pushed at all.
export const fetchAllAgentsForAdmin = () => apiGet('/api/remote-tools/agents/all');
// What is in the installer folder, and which build Omni serves (admin).
export const fetchInstallerList = () => apiGet('/api/remote-tools/installer/list');
// Lock a device out / let it back in. Deactivation keeps the device key and
// refuses it at the auth + enrolment gates, so the agent cannot re-enrol back in.
// NOTE: apiPost(path, opts) serialises opts.body — a bare object is treated as
// request options and sends no body at all.
export const setAgentActiveState = (hostname, active) =>
  apiPost(`/api/remote-tools/agents/${encodeURIComponent(hostname)}/active`, { body: { active } });
// Clear one device's key so its next enrolment is accepted without proof —
// recovers a device stuck offline because it can no longer prove who it is.
// Keeps the record, history and cached snapshot (unlike Delete).
export const resetAgentEnrolment = (hostname) =>
  apiPost(`/api/remote-tools/agents/${encodeURIComponent(hostname)}/reset-enrolment`);
// Remove the enrolment record (and its queued jobs). Does NOT stop a running
// device — it will re-enrol and reappear. For a machine that is gone.
export const deleteAgentRecord = (hostname) =>
  apiDelete(`/api/remote-tools/agents/${encodeURIComponent(hostname)}`);
// Push a reinstall of the agent software. The device downloads the installer
// itself and hands it to a one-shot task, so enrolment is preserved.
export const reinstallAgentSoftware = (hostname) =>
  apiPost(`${base(hostname)}/reinstall`, { body: {} });
/**
 * Queue a FIRST install of the agent onto a device that has none, by adding its
 * Entra device object to the group an Intune Required assignment installs from.
 * Addressed by Intune device id, not hostname — a device with no agent has no
 * entry in the hostname-keyed registry the other calls resolve against.
 *
 * Resolves with { success, alreadyQueued, deviceName, groupName, message }: a
 * refusal the operator can act on (wrong platform, no Entra record) comes back
 * as 200 success:false, so check `success`, not just for a thrown error.
 */
export const requestAgentInstall = (intuneDeviceId) =>
  apiPost('/api/remote-tools/install-agent', { body: { intuneDeviceId } });
export const fetchRemoteSnapshot = (hostname) => apiGet(`${base(hostname)}/snapshot`);
export const fetchRemoteLiveData = (hostname) => apiGet(`${base(hostname)}/live-data`);

/**
 * Resolve the enrolled agent for an Intune device. Needed because Android
 * agents are keyed by `android-<serial>`, not the Intune deviceName — pass
 * what the device record has and use the returned `agent.hostname` as the
 * registry key for every other call. Resolves to null when the device has no
 * agent — the server answers 200 with a null agent for that, rather than a 404
 * the browser would log for every device row.
 */
export async function resolveRemoteAgent({ hostname, serial, intuneDeviceId } = {}) {
  try {
    const res = await apiGet(`/api/remote-tools/resolve${buildQuery({ hostname, serial, intuneDeviceId })}`);
    return res?.agent ?? null;
  } catch {
    // Kept for genuine failures: null is still the right answer for a row
    // decoration (it simply shows no Remote Tools button).
    return null;
  }
}

/* ============================================================
 * Actions
 * ============================================================ */

export const killRemoteProcess    = (hostname, pid)          => apiPost(`${base(hostname)}/kill-process`, { body: { pid } });
// `queue: true` in the body queues the action for an offline device (202 + job)
// instead of executing live — see the offline job queue endpoints below.
export const controlRemoteService = (hostname, action, name, queue = false) => apiPost(`${base(hostname)}/service`, { body: { action, name, queue } });
export const controlRemoteStartup = (hostname, body)         => apiPost(`${base(hostname)}/startup`, { body });
export const runRemoteScript      = (hostname, body)         => apiPost(`${base(hostname)}/run-script`, { body });
// Uninstall installed software. `body` carries the software's uninstall-registry
// fields ({ id, name, isMsi, uninstallString, quietUninstallString, scope,
// queue }). Machine apps run as SYSTEM; scope:'user' apps run in the logged-in
// user's session. `queue: true` defers to reconnect for offline devices.
export const uninstallRemoteSoftware = (hostname, body)      => apiPost(`${base(hostname)}/uninstall-software`, { body });

/* ============================================================
 * Windows Update tab
 * ============================================================ */

// Live WU COM scan for pending updates — can take 1-5 minutes; no frontend
// timeout is applied (see api/client.js), matching the proven 10-minute
// Run Script budget.
// Drives tab — test each mapped drive as the signed-in user (read-only).
export const checkDriveAccess       = (hostname)       => apiPost(`${base(hostname)}/drives/check-access`);
// Printers tab — installed printers (everyone vs one person), queues, stuck jobs.
export const listDevicePrinters     = (hostname)       => apiGet(`${base(hostname)}/printers`);
// body: { printerName, jobId? } — leave jobId out to delete every job on that printer.
export const removePrintJobs        = (hostname, body) => apiPost(`${base(hostname)}/printers/jobs/remove`, { body });
export const resetPrintSpooler      = (hostname)       => apiPost(`${base(hostname)}/printers/reset-spooler`);
// body: { printerName, scope: 'all-users'|'per-user', userSid? } — per-user needs the person's SID.
export const removeDevicePrinter    = (hostname, body) => apiPost(`${base(hostname)}/printers/remove`, { body });
// body: { printerName, userSid } — sets that signed-in person's default printer.
export const setDefaultPrinter      = (hostname, body) => apiPost(`${base(hostname)}/printers/default`, { body });
export const checkWindowsUpdates    = (hostname)       => apiPost(`${base(hostname)}/windows-updates/check`);
export const listInstalledUpdates   = (hostname)       => apiGet(`${base(hostname)}/windows-updates/installed`);
// body: { kb, queue }. `queue: true` defers to reconnect for offline devices
// (reuses the "script" job type — appears in the Jobs tab like Run Script).
export const uninstallWindowsUpdate = (hostname, body) => apiPost(`${base(hostname)}/windows-updates/uninstall`, { body });
// body: { kb, queue }. Downloads + installs a pending KB found in the last
// Check for Updates scan — can take several minutes for large updates.
export const installWindowsUpdate   = (hostname, body) => apiPost(`${base(hostname)}/windows-updates/install`, { body });
export const checkWindowsUpdatesInBackground = (hostname) => apiPost(`${base(hostname)}/windows-updates/check`, { body: { background: true } });
export const fetchWindowsUpdateJob  = (hostname, jobId, kind) => apiGet(`${base(hostname)}/windows-updates/jobs/${encodeURIComponent(jobId)}?kind=${encodeURIComponent(kind)}`);
// Entirely Graph-based (Intune device + policy cache) — works even when the
// device is offline, unlike the KB actions above which need a live agent.
export const getUpdateRingStatus    = (hostname)       => apiGet(`${base(hostname)}/windows-updates/update-ring`);
// body: { targetGroupId }. Removes the device from its current ring's Entra
// group and adds it to the target ring's group.
export const moveUpdateRing         = (hostname, body) => apiPost(`${base(hostname)}/windows-updates/update-ring`, { body });

/* ============================================================
 * Intune Compliance tab
 * ============================================================ */

// Graph-based per-policy / per-setting compliance state — works offline.
// `refresh=true` forces a blocking re-read (the Re-check button).
export const getIntuneCompliance     = (hostname, refresh) => apiGet(`${base(hostname)}/intune-compliance${refresh ? '?refresh=true' : ''}`);
// Graph syncDevice only — works offline; a 403 means the app registration lacks the sync permission.
export const syncIntuneDevice        = (hostname)       => apiPost(`${base(hostname)}/intune-compliance/sync`, { body: {} });
// Device-side read-only check via the agent (needs the device online, ~1-2 minutes).
export const runComplianceDiagnostic = (hostname)       => apiPost(`${base(hostname)}/intune-compliance/diagnose`, { body: {} });
// body: { fixId, queue }. Runs the one-click fix on the device then asks Graph to sync it.
export const applyComplianceFix      = (hostname, body) => apiPost(`${base(hostname)}/intune-compliance/fix`, { body });
// Temporary policy exclusions: adds the device to the policy's existing exclusion
// group; Omni removes it again after `hours` (default 48).
export const listComplianceExclusions = (hostname, policyIds) => apiGet(`${base(hostname)}/intune-compliance/exclusions${policyIds?.length ? `?policyIds=${encodeURIComponent(policyIds.join(','))}` : ''}`);
export const excludeFromPolicy        = (hostname, body)     => apiPost(`${base(hostname)}/intune-compliance/exclusions`, { body });
export const endComplianceExclusion   = (hostname, id)       => apiDelete(`${base(hostname)}/intune-compliance/exclusions/${encodeURIComponent(id)}`);

/* ============================================================
 * Registry tab (Windows, agent v2.3.0+)
 * ============================================================ */

// Browse a key → { ok, hive, path, subkeys:[{name,hasChildren}], values:[{name,type,data}], error? }.
export const readRegistry       = (hostname, hive, path) => apiGet(`${base(hostname)}/registry${buildQuery({ hive, path })}`);
// On-demand backup of a key → { ok, backup:{ fileName, contentBase64?, savedPath, truncated? }, error? }.
export const backupRegistryKey  = (hostname, body) => apiPost(`${base(hostname)}/registry/backup`, { body });
// Add or edit a value. body: { hive, path, name, type, data }. The key is
// auto-backed-up first; the response carries that backup for download.
export const saveRegistryValue  = (hostname, body) => apiPost(`${base(hostname)}/registry/value`, { body });
// Add a key. body: { hive, path, keyName }.
export const addRegistryKey     = (hostname, body) => apiPost(`${base(hostname)}/registry/key`, { body });
// Delete a value. body: { hive, path, name } — auto-backup, returned in the response.
export const deleteRegistryValue = (hostname, body) => apiDelete(`${base(hostname)}/registry/value`, { body });
// Delete a key + everything under it. body: { hive, path } — auto-backup, returned in the response.
export const deleteRegistryKey  = (hostname, body) => apiDelete(`${base(hostname)}/registry/key`, { body });

/**
 * Save a registry backup (returned in a mutation/backup response) to a `.reg`
 * file via the browser. `contentBase64` is the raw exported file bytes
 * (UTF-16LE); when it's absent (export exceeded the transport cap) the backup
 * still exists on the device at `backup.savedPath`, so there's nothing to save
 * here — the caller should surface that path instead.
 *
 * @param {{ fileName?: string, contentBase64?: string }} backup
 * @returns {boolean} true when a download was triggered
 */
export function saveRegistryBackupToFile(backup) {
  if (!backup?.contentBase64) return false;
  const bin = atob(backup.contentBase64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/octet-stream' });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = backup.fileName || 'registry-backup.reg';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  return true;
}

/* ============================================================
 * Offline job queue
 * ============================================================ */

export const fetchRemoteJobs = (hostname)        => apiGet(`${base(hostname)}/jobs`);
export const cancelRemoteJob = (hostname, jobId) => apiDelete(`${base(hostname)}/jobs/${encodeURIComponent(jobId)}`);
export const launchRemoteHelp     = (hostname)                => apiPost(`${base(hostname)}/remote-help`);
// Local account enable/disable + local group membership. `member` accepts
// local names, DOMAIN\user, and AzureAD\user@domain.com (Entra-joined devices).
export const controlLocalUser     = (hostname, action, name)          => apiPost(`${base(hostname)}/local-user`, { body: { action, name } });
export const controlLocalGroup    = (hostname, action, group, member) => apiPost(`${base(hostname)}/local-group`, { body: { action, group, member } });
// runAs: 'system' (default) or 'user' — open the interactive terminal in the
// logged-in user's session (host pools / servers with active sessions).
// `user` (optional, with runAs 'user') targets a specific logged-on user's
// session (DOMAIN\user) on multi-session hosts; omitted → console user.
export const startRemoteShell     = (hostname, shellType, runAs = 'system', user = undefined) => apiPost(`${base(hostname)}/shell`, { body: { shellType, runAs, ...(user ? { user } : {}) } });
// Interactive logon sessions (console + RDP, full DOMAIN\user names) for the
// terminal's run-as-user picker. 502s when the agent is older than 2.2.0 —
// callers fall back to the generic "Logged-in user" mode.
export const fetchRemoteSessions  = (hostname) => apiGet(`${base(hostname)}/sessions`);

// Support chat (technician <-> the person at the device)
export const startRemoteChat      = (hostname, user = undefined, ticketId = undefined) => apiPost(`${base(hostname)}/chat`, { body: { ...(user ? { user } : {}), ...(ticketId ? { ticketId } : {}) } });
export const fetchDylanChatRead   = (hostname, body) => apiPost(`${base(hostname)}/v2/assist/chat`, { body });
export const fetchRemoteChats     = (hostname) => apiGet(`${base(hostname)}/chats`);
export const fetchRemoteChat      = (hostname, chatId) => apiGet(`${base(hostname)}/chats/${encodeURIComponent(chatId)}`);
// Per-app support actions (Android). action 'uninstall' (attended; blocked for
// Intune-Required apps) or 'open_info' (deep-link App Info → reset via Screen
// Control). Resolves { success, message }.
export const remoteAppAction      = (hostname, action, packageName) => apiPost(`${base(hostname)}/app`, { body: { action, packageName } });

// Attended screen view (Android). Returns { sessionId, codec }; the caller opens
// the operator WS, sends admin:subscribe_screen, and renders the frames. codec
// 'h264' streams real-time hardware video (decoded via WebCodecs); 'jpeg' is the
// universal still-frame fallback.
export const startRemoteScreen    = (hostname, codec = 'jpeg')  => apiPost(`${base(hostname)}/screen`, { body: { codec } });

/* ============================================================
 * Script library (admin-managed, selectable in Run Script)
 * ============================================================ */

export const fetchScriptLibrary = ()     => apiGet('/api/remote-tools/scripts');
export const saveLibraryScript  = (body) => apiPost('/api/remote-tools/scripts', { body }); // admin only
export const deleteLibraryScript = (id)  => apiDelete(`/api/remote-tools/scripts/${encodeURIComponent(id)}`); // admin only

// Dylan lessons — fixes the team saves for Dylan to reuse on similar tickets.
export const fetchDylanLessons  = ()         => apiGet('/api/remote-tools/dylan-lessons');
export const saveDylanLesson    = (body)     => apiPost('/api/remote-tools/dylan-lessons', { body });
export const updateDylanLesson  = (id, body) => apiPut(`/api/remote-tools/dylan-lessons/${encodeURIComponent(id)}`, { body }); // admin only
export const deleteDylanLesson  = (id)       => apiDelete(`/api/remote-tools/dylan-lessons/${encodeURIComponent(id)}`); // admin only

/* ============================================================
 * Files
 * ============================================================ */

export const listRemoteFiles       = (hostname, path)       => apiGet(`${base(hostname)}/files${buildQuery({ path })}`);
export const fetchRemoteFolderSize = (hostname, path)       => apiGet(`${base(hostname)}/files/folder-size${buildQuery({ path })}`);
export const deleteRemoteFile      = (hostname, path)       => apiDelete(`${base(hostname)}/files${buildQuery({ path })}`);
export const renameRemoteFile      = (hostname, from, to)   => apiPut(`${base(hostname)}/files/rename`, { body: { from, to } });

export const uploadRemoteFile = (hostname, destDir, file) => {
  const form = new FormData();
  form.append('file', file);
  return apiPost(`${base(hostname)}/files/upload${buildQuery({ path: destDir })}`, { body: form });
};

/**
 * Downloads a remote file as an authenticated blob fetch (an `<a>` tag
 * can't carry an Authorization header) and triggers a browser save via a
 * temporary object URL.
 *
 * @param {string} hostname
 * @param {string} path
 */
export async function downloadRemoteFile(hostname, path) {
  const token = await acquireApiToken();
  const url = `${API_BASE_URL}${base(hostname)}/files/download${buildQuery({ path })}`;
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const payload = await res.json();
      message = payload?.error?.message || payload?.error || payload?.message || message;
    } catch { /* not JSON — keep default message */ }
    throw new Error(message);
  }
  const blob = await res.blob();
  const name = path.split(/[\\/]/).pop() || 'download';
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
}

/**
 * Downloads the current Omni-hosted agent installer (Azure Blob-backed) as an
 * authenticated blob fetch, same pattern as downloadRemoteFile above — a
 * plain `<a href>` can't carry the Authorization header, and the blob's SAS
 * URL is never sent to the browser: Omni fetches it server-side and streams
 * the bytes through. The server resolves the newest build in the configured
 * folder, so this always fetches the current one.
 *
 * @param {string} [fileName] display filename to save as, if known
 */
export async function downloadRemoteToolsInstaller(fileName) {
  const token = await acquireApiToken();
  const url = `${API_BASE_URL}/api/remote-tools/installer/download`;
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const payload = await res.json();
      message = payload?.error?.message || payload?.error || payload?.message || message;
    } catch { /* not JSON — keep default message */ }
    throw new Error(message);
  }
  const blob = await res.blob();
  const name = fileName || 'OmniAgent.msi';
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
}

/* ============================================================
 * Admin shell WebSocket (Remote Terminal pop-out)
 * ============================================================ */

/**
 * Builds the ws:// or wss:// URL for the shared agent-relay WebSocket,
 * with a fresh bearer token attached as a query param (WebSocket can't
 * set custom headers). Derived from the same API_BASE_URL the rest of
 * the api client resolves against, so it works whether the frontend is
 * same-origin with the API or not.
 *
 * @returns {Promise<string>}
 */
export async function buildAgentWsUrl() {
  const token = await acquireApiToken();
  const httpBase = API_BASE_URL.replace(/\/$/, '');
  const wsBase = httpBase.replace(/^https/i, 'wss').replace(/^http/i, 'ws');
  return `${wsBase}/api/agent-ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
}

/* ============================================================
 * New Remote Access Tools (preview "remote-tools-v2")
 * Only people on the preview list can call these (server-enforced).
 * ============================================================ */

export const fetchDeviceOverview   = (hostname, upn)     => apiGet(`${base(hostname)}/v2/overview${buildQuery({ upn: upn || undefined })}`);
export const fetchDeviceTickets    = (hostname, upn, refresh) => apiGet(`${base(hostname)}/v2/tickets${buildQuery({ upn: upn || undefined, refresh: refresh ? 'true' : undefined })}`);
export const fetchDeviceTicket     = (hostname, id)      => apiGet(`${base(hostname)}/v2/tickets/${encodeURIComponent(id)}`);
export const fetchTicketScreenshots = (hostname, id)     => apiGet(`${base(hostname)}/v2/tickets/${encodeURIComponent(id)}/screenshots`);
export const fetchTicketThreadImages = (hostname, id, srcs) => apiPost(`${base(hostname)}/v2/tickets/${encodeURIComponent(id)}/images`, { body: { srcs } });
export const fetchDylanSuggestions = (hostname, body)    => apiPost(`${base(hostname)}/v2/assist/suggestions`, { body });
export const fetchDylanTicketRead  = (hostname, id, body) => apiPost(`${base(hostname)}/v2/assist/tickets/${encodeURIComponent(id)}`, { body });
